<template>
  <div v-if="activeTab === 'Dimensions'" class="content-wrapper">
    <input
      v-model="searchTerm"
      type="text"
      class="search-input"
      placeholder="Search Filters..."
    />
    <div class="layout-contents">
      <simplebar class="layout-contents" data-auto-hide="true">
        <div class="contents">
          <div
            v-for="(filter, index) in filteredOptions"
            :key="index"
            class="filter-item"
            @click="toggleEntityFilter(filter)"
          >
            <div class="flex-col">
              <h4 class="ellipsis">{{ filter.name }}</h4>
              <p class="ellipsis">{{ filter.datasource_name }}</p>
            </div>
            <icon
              type="button"
              :class="['check-mark', { active: filter.isActive }]"
              @click="toggleEntityFilter(filter)"
              >{{
                filter.isActive ? "check_box" : "check_box_outline_blank"
              }}</icon
            >
          </div>
        </div>
      </simplebar>
    </div>
  </div>
  <div v-else class="content-wrapper">
    <div v-if="measureOptions.length > 0" class="measure-layout">
      <label>Measure property</label>
      <SearchableDropDown
        v-model="measure"
        :options="measureOptions"
        placeholder="Choose a measure"
      />
      <label>Comparison</label>
      <DropDown
        v-model="comparison"
        :options="comparisons"
        placeholder="Choose Comparison"
      />
      <label>Value</label>
      <input type="text" v-model="compValue" placeholder="Comparison value" />
      <div class="prop-actions">
        <expand />
        <button class="prop-action-button save" @click="applyMeasureFilter">
          <Icon size="18">check</Icon>add filter
        </button>
      </div>
    </div>
    <p v-else class="help-text">
      The selected entity doesn't support measure filters
    </p>
  </div>
</template>

<script>
import Button from "@/components/global/views/Button";
import SearchableDropDown from "@/components/metrics/views/SearchableDropDown";
import Categorical, {
  ActiveCategory,
  CategoricalItem,
} from "@/models/Categorical.model";
import { mapActions } from "vuex";

export default {
  components: {
    Button,
    SearchableDropDown,
  },
  props: {
    options: Array,
    activeTab: String,
    model: Object,
  },
  data() {
    return {
      showFilters: false,
      searchTerm: "",
      itemsChanged: false,
      measure: null,
      comparison: null,
      compValue: null,
      comparisons: [
        {
          name: "Less than",
          id: "<",
        },
        {
          name: "Greater than",
          id: ">",
        },
        {
          name: "Equal to",
          id: "=",
        },
      ],
    };
  },
  computed: {
    filteredOptions() {
      if (!this.options) return null;
      const regex = new RegExp(this.searchTerm, "i");
      return this.options
        .filter((item) => item.is_dimension)
        .filter((item) => regex.test(item.name))
        .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
        .sort((a, b) =>
          a.datasource_name && b.datasource_name
            ? a.datasource_name > b.datasource_name
              ? 1
              : b.datasource_name > a.datasource_name
              ? -1
              : 0
            : 0
        );
    },
    measureOptions() {
      if (!this.options) return null;
      return this.options.filter((item) => item.is_measure);
    },
  },
  methods: {
    ...mapActions("FilterManager", ["createCustomFilter"]),
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    toggleEntityFilter(filter) {
      this.itemsChanged = true;
      this.$emit("toggle", filter);
    },
    applyMeasureFilter() {
      const appliedFilter = new ActiveCategory({
        category_name: this.measure.name,
        category_id: this.measure.id,
        item_id: parseInt(this.compValue),
        comparison: this.comparison.id,
        entity_model_id: this.model.id,
      });
      this.createCustomFilter(this.measure);
      this.$emit("toggleFilter", appliedFilter);
      this.$emit("close");
      //this.$emit('change');
    },
  },
  watch: {
    showFilters(value) {
      if (!value) {
        if (this.itemsChanged) {
          this.$emit("change");
        }
      } else {
        this.itemsChanged = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.content-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  overflow: hidden;
  background: var(--color-secondary);
}
.search-input {
  margin: 5px;
}

.help-text {
  margin: 0;
  padding: 30px;
  text-align: center;
  opacity: 0.7;
}

.layout-contents {
  min-width: 0;
  height: 100%;
  flex: 1;
  overflow-y: auto;
  position: relative;
}

.measure-layout {
  display: flex;
  flex-direction: column;
  padding: 10px;

  label {
    padding: 5px 0;
  }

  .prop-actions {
    display: flex;
    flex-direction: row;
    padding: 15px 0 0 0;

    .prop-action-button {
      display: flex;
      align-items: center;
      padding: 10px;
      border: 0;
      border-radius: 3px;
      background: var(--color-default-background);
      color: var(--font-color);
      text-transform: capitalize;
      transition: 0.3s all ease-in-out;

      &.save {
        background-color: var(--color-primary);
        color: var(--color-secondary);
        margin-left: 10px;

        &:hover {
          opacity: 0.8;
          color: var(--color-secondary);
        }
      }

      i {
        margin-right: 5px;
        pointer-events: none;
      }

      &:hover {
        cursor: pointer;
        color: var(--color-error);
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }
}

.contents {
  flex: 1;
  padding: 5px 0;
  overflow: auto;

  .filter-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--border-color);
    padding: 10px;

    &:last-child {
      border-bottom: 0;
    }

    p {
      margin: 0;
      opacity: 0.6;
      font-size: 0.9em;
    }

    .check-mark {
      opacity: 0.3;

      &.active {
        opacity: 1;
        color: var(--color-primary);
      }
    }

    &:hover {
      cursor: pointer;
      background: var(--color-primary-light);
    }
  }
}
</style>
